import { Box, DialogContent, Typography } from "@mui/material";
import {
  AlertsBarContainer,
  AlertsBarVariant,
  AlertsContext,
  DistaDialog,
  DistaDialogHeader,
} from "@sista/dista-ui";
import {
  Organization,
  OrganizationRegistrationRequestDTO,
  Relation,
  RelationDTO,
  UiContext,
  useEmailValidation,
  useMaxLengthValidation,
  useMinLengthValidation,
  usePhoneNumberValidation,
  useRequiredValidation,
} from "@sista/library-stasi";
import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ForegnOrganizationCreateOrUpdateContent } from "./ForeignOrganizationCreateOrUpdateContent";
import { OrganizationCreateDelegateContent } from "./OrganizationCreateDelegateContent";
import { OrganizationRejstrikSearchContent } from "./OrganizationRejstrikSearchContent";
import { OrganizationRequestToJoinDialog } from "./OrganizationRequestToJoinDialog";
import { useControlHandle } from "../../../utils/controlHandle";

export type OrganizationFindForm = {
  idNumber?: string;
};

export type OrganizationEditDialogProps = {
  organization?: Organization;
  registration?: OrganizationRegistrationRequestDTO;
};

export const OrganizationRegisterDialog = (
  props: OrganizationEditDialogProps
) => {
  const { closeDialogs } = useContext(UiContext);
  const alerts = useContext(AlertsContext);
  const { t } = useTranslation("ucet");
  const navigate = useNavigate();

  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>(props.organization);
  const [selectedOrganizationRelations, setSelectedOrganizationRelations] =
    useState<RelationDTO[]>(null);
  const [isForeignCompanyChecked, setIsForeignCompanyChecked] = useState(null);

  const phone = usePhoneNumberValidation();
  const email = useEmailValidation();
  const maxLength = useMaxLengthValidation(37);
  const maxInfoLength = useMaxLengthValidation(1000);
  const minLength = useMinLengthValidation(2);
  const required = useRequiredValidation();
  const backHandler = useControlHandle();

  const phaseFound = selectedOrganization?.id != null;
  const phaseVerified = selectedOrganization?.verified ?? false;

  const closeDialog = useCallback(() => {
    navigate("/");
    closeDialogs();
  }, [closeDialogs, navigate]);

  return (
    <DistaDialog onClose={closeDialog} open={true}>
      <DistaDialogHeader
        onClose={closeDialog}
        onBack={backHandler.callback}
        showBackButton={backHandler.callback != null}
        title={t(
          "ucet.organization.connectToProfile",
          "Propojení s organizací"
        )}
      />
      <Box sx={{ flexShrink: 0 }}>
        <AlertsBarContainer
          id="alerts-in-dialog"
          variant={AlertsBarVariant.relative}
        />
      </Box>
      <DialogContent>
        {!phaseFound && isForeignCompanyChecked == null ? (
          <OrganizationRejstrikSearchContent
            onFoundOrganization={setSelectedOrganization}
            onFoundRelationsInOrganization={setSelectedOrganizationRelations}
            isForeignCompanyChecked={setIsForeignCompanyChecked}
          />
        ) : null}
        {isForeignCompanyChecked === false ? (
          <ForegnOrganizationCreateOrUpdateContent
            organization={selectedOrganization}
            onOrganizationDone={setSelectedOrganization}
            onRelationFound={setSelectedOrganizationRelations}
            onIsForeignCompanyChecked={setIsForeignCompanyChecked}
          />
        ) : null}
        {phaseFound && isForeignCompanyChecked !== false && !phaseVerified ? (
          <>
            <OrganizationCreateDelegateContent
              organization={selectedOrganization}
              registration={props.registration}
              backControl={backHandler}
            />
          </>
        ) : null}
        {phaseFound && phaseVerified && isForeignCompanyChecked !== false ? (
          <OrganizationRequestToJoinDialog
            organization={selectedOrganization}
            onNewIco={() => {
              setIsForeignCompanyChecked(null);
              return setSelectedOrganization(null);
            }}
          />
        ) : null}
      </DialogContent>
    </DistaDialog>
  );
};
